// What the signed-in person may do, per backend module.
//
// GET /api/staff/me/permissions → { exempt, modules: { Dashboard, CRM, Patients,
// Appointments, 'Treatment plans', WhatsApp, Marketing, Support, 'AI agents',
// Wallet, Settings: 'none'|'view'|'edit' } } — the same facts the backend's
// staff-permissions middleware enforces (owner / staff admin / no staff row =
// exempt). Settings reads are never gated, so this read always works for a
// signed-in user. Anything that can't be read counts as 'edit', as the backend
// does for a module never set, so a failed check never hides a working
// button; the global PermissionNotice toast still explains a refusal.
import { useEffect, useState } from "react";
import { fetchApi } from "./marketing/access";

const TTL = 60 * 1000; // the backend caches permissions for 60 s too
const LEVELS = ["none", "view", "edit"];
let cached = null; // { token, at, promise } → { exempt, modules, known }

async function readPermissions() {
  const res = await fetchApi("/api/staff/me/permissions");
  if (!res.ok) return { exempt: false, modules: {}, known: false };
  const d = res.data?.data || {};
  return { exempt: !!d.exempt, modules: d.modules || {}, known: true };
}

/** The cached answer (one request per minute per login). */
export function loadMyPermissions() {
  const token = localStorage.getItem("token");
  if (!cached || cached.token !== token || Date.now() - cached.at > TTL) {
    cached = { token, at: Date.now(), promise: readPermissions().catch(() => ({ exempt: false, modules: {}, known: false })) };
  }
  return cached.promise;
}

/** Forget the cached answer (after a 403 says it changed, or after saving someone's permissions). */
export const resetMyPermissions = () => { cached = null; };

/** 'none' | 'view' | 'edit' for one module of a loaded answer. */
export const levelOf = (perms, module) => {
  if (!perms || perms.exempt) return "edit";
  const v = perms.modules?.[module];
  return LEVELS.includes(v) ? v : "edit";
};

/**
 * { loading, level, canEdit, viewOnly, noAccess, exempt } for one module
 * ("CRM", "Patients", "Appointments", "Treatment plans", "WhatsApp",
 * "Marketing", "Settings", …). While loading everything stays enabled, so
 * nobody sees buttons flicker off.
 */
export function useModulePermission(module) {
  const [state, setState] = useState({ loading: true, level: "edit", exempt: false });
  useEffect(() => {
    let live = true;
    loadMyPermissions().then((p) => live && setState({ loading: false, level: levelOf(p, module), exempt: !!p.exempt }));
    return () => { live = false; };
  }, [module]);
  return {
    loading: state.loading,
    level: state.level,
    canEdit: state.level === "edit",
    viewOnly: state.level === "view",
    noAccess: state.level === "none",
    exempt: state.exempt,
  };
}
